'use client';

import {Box, styled, IconButton, Button, ButtonBase, ButtonProps} from '@mui/material';
import {StyledSectionContainer} from '@/components/sections/shared/Section.styles';

// Single source of truth for the slide animation duration. JS timing logic
// (snap-back, drag-release) and the CSS transition must stay in sync.
export const SLIDE_DURATION_MS = 500;

export const StyledCarouselSectionContainer = styled(StyledSectionContainer)(({theme}) => ({
  width: '100%',
  backgroundColor: theme.palette.background.lightGrey,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

export const StyledCarouselWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: theme.breakpoints.values.lg,
  margin: '0 auto',
  // reveal chevrons on hover or keyboard focus within the card
  '&:hover .chevronBtn, &:focus-within .chevronBtn': {
    opacity: 1,
    pointerEvents: 'auto',
    transform: 'translateY(-50%) scale(1)',
  },
}));

export const StyledCarouselCard = styled(Box)(({theme}) => ({
  position: 'relative',
  height: 500,
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadiusLg,
  // Below md the card switches to column-reverse and its height is set imperatively
  // from the active slide's measured height (--card-h), so the red content area hugs
  // each slide's real content instead of padding to a uniform block.
  [theme.breakpoints.down('md')]: {
    height: 'var(--card-h, auto)',
    backgroundColor: '#750308',
    transition: `height ${SLIDE_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
    touchAction: 'pan-y',
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
  [theme.breakpoints.down('mobile')]: {
    // The wrapper keeps the red background so the gap between the rounded
    // white slides still reads as the same red card family.
    borderRadius: 0,
    '&::before, &::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      width: '7.5%',
      zIndex: 2,
      pointerEvents: 'none',
    },
    '&::before': {
      left: 0,
      background: 'linear-gradient(to right, rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0))',
    },
    '&::after': {
      right: 0,
      background: 'linear-gradient(to left, rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0))',
    },
  },
}));

export const StyledCarouselTrack = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isTransitioning' && prop !== 'isDragging',
})<{isTransitioning?: boolean; isDragging?: boolean}>(({theme, isTransitioning, isDragging}) => ({
  display: 'flex',
  height: '100%',
  willChange: 'transform',
  transform: 'translateX(calc(var(--current, 0) * -100% + var(--drag-px, 0px)))',
  transition: !isDragging && isTransitioning
    ? `transform ${SLIDE_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
    : 'none',
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
  // Below md each slide sizes to its own content, so don't stretch siblings to the tallest.
  [theme.breakpoints.down('md')]: {
    alignItems: 'flex-start',
    height: 'auto',
  },
  [theme.breakpoints.down('mobile')]: {
    // Each slide is 85% wide below the mobile breakpoint; center current slide with a 7.5% peek on each side.
    transform: 'translateX(calc(7.5% + var(--current, 0) * -85% + var(--drag-px, 0px)))',
  },
}));

export const StyledCarouselTrackSlide = styled(Box)(({theme}) => ({
  flex: '0 0 100%',
  height: '100%',
  [theme.breakpoints.down('md')]: {
    height: 'auto',
  },
  [theme.breakpoints.down('mobile')]: {
    flex: '0 0 85%',
    paddingLeft: theme.spacing(0.75),
    paddingRight: theme.spacing(0.75),
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    boxSizing: 'border-box',
  },
}));

export const StyledCarouselSlideLayout = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  height: '100%',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column-reverse',
    height: 'auto',
  },
  [theme.breakpoints.down('mobile')]: {
    borderRadius: theme.shape.borderRadiusLg,
    overflow: 'hidden',
  },
}));

export const StyledCarouselImageHalf = styled(Box)(({theme}) => ({
  position: 'relative',
  flex: '1 1 50%',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    flex: '0 0 400px',
    width: '100%',
  },
  [theme.breakpoints.down('sm')]: {
    flex: '0 0 280px',
  },
}));

export const StyledCarouselContentHalf = styled(Box)(({theme}) => ({
  flex: '1 1 50%',
  backgroundColor: '#750308',
  color: '#fff',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  padding: theme.spacing(5, 6),
  [theme.breakpoints.down('md')]: {
    flex: '0 0 auto',
    width: '100%',
    padding: theme.spacing(3),
    overflow: 'hidden',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2.25),
  },
  [theme.breakpoints.down('mobile')]: {
    backgroundColor: theme.palette.background.white,
    color: theme.palette.text.dark,
    // Layout is column-reverse on mobile, so a top border here divides the
    // image (visually above) from the content (visually below).
    borderTop: `1px solid ${theme.palette.background.grey}`,
  },
}));

export const StyledCarouselChevronBtn = styled(IconButton)(({theme}) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%) scale(0.9)',
  opacity: 0,
  pointerEvents: 'none',
  transition: 'opacity 160ms ease, transform 160ms ease',
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
  backgroundColor: 'rgba(255,255,255,0.3)',
  '&:hover': { backgroundColor: 'rgba(255,255,255,0.5)' },
  // On tablet (column-reverse) there's no hover, so force chevrons on.
  [theme.breakpoints.down('md')]: {
    opacity: 1,
    pointerEvents: 'auto',
    transform: 'translateY(-50%) scale(1)',
  },
  [theme.breakpoints.down('mobile')]: {
    display: 'none',
  },
}));

export const StyledCarouselDots = styled(Box)(({theme}) => ({
  position: 'absolute',
  bottom: 16,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: 8,
  [theme.breakpoints.down('mobile')]: {
    display: 'none',
  },
}));

export const StyledCarouselDot = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({theme, active}) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  padding: 0,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
  backgroundColor: active ? '#fff' : 'rgba(255,255,255,0.5)',
  transform: active ? 'scale(1.2)' : 'scale(1)',
  '&:focus-visible': {
    outline: '2px solid #fff',
    outlineOffset: 2,
  },
  [theme.breakpoints.down('md')]: {
    width: 8,
    height: 8,
  },
  [theme.breakpoints.down('sm')]: {
    width: 6,
    height: 6,
  },
}));

export const StyledCarouselInformationLinkButton = styled(Button)<ButtonProps<'a'>>(({theme}) => ({
  marginTop: 24,
  width: 'fit-content',

  [theme.breakpoints.down('sm')]: {
    marginTop: 12,
  },

  // The arrow icon only shows on the smallest breakpoint where the button
  // becomes a plain text link.
  [theme.breakpoints.up('mobile')]: {
    '& .MuiButton-endIcon': {
      display: 'none',
    },
  },

  [theme.breakpoints.down('mobile')]: {
    backgroundColor: 'transparent',
    color: theme.palette.text.linkSecondary,
    padding: 0,
    minWidth: 0,
    borderRadius: 0,
    marginTop: 0,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
      color: theme.palette.text.linkSecondary,
    },
    '& .MuiButton-endIcon': {
      marginLeft: theme.spacing(0.5),
    },
  },
}));